import { faCheck, faEllipsis, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RouteRebookingKindEnum, RouteRebookingProposalDTO } from 'api/model';
import TransparentSpinner from 'components/spinners/TransparentSpinner';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useOnClickOutside } from 'usehooks-ts';
import {
  atClockOnSameDay,
  ConfirmedRebooking,
  formatDayHours,
  RebookingAnswer,
  rebookingContacts,
} from './rebooking-helpers';
import { formatClock } from './selection-helpers';

/** Bredden på svarskolumnen. Sätts på alla ben i ett paket med förslag, så att tiderna linjerar. */
export const REBOOKING_ACTIONS_WIDTH_PX = 132;

const ContactRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 3px;
`;

const CallPill = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 26px;
  padding: 0 10px;
  border: 1px solid rgb(235, 205, 184);
  border-radius: 999px;
  background: #fff;
  color: rgb(126, 58, 20);
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: rgb(251, 241, 234);
  }
`;

const SecondaryPhone = styled.a`
  font-size: 12px;
  white-space: nowrap;
`;

const Note = styled.div<{ tone?: 'good' }>`
  font-size: 0.85em;
  color: ${({ tone }) => (tone === 'good' ? 'rgb(29, 107, 56)' : 'rgb(90, 90, 90)')};
`;

const Muted = styled.span`
  font-size: 12px;
  color: rgb(120, 120, 120);
`;

interface ContactProps {
  proposal: RouteRebookingProposalDTO;
  /** Benets nummer för det andra förslaget i ett par, när det ligger i samma paket. */
  pairedLegNumber?: number;
}

/** Under benets adress: vad flytten gäller och vem som ska ringas. */
export const LegRebookingContact: React.FC<ContactProps> = ({
  proposal: r,
  pairedLegNumber,
}) => {
  const { primary, secondary } = rebookingContacts(r);
  const paired = r.pairedRegistrationNumber ?? (r.pairedAssignmentId ? `uppdrag ${r.pairedAssignmentId}` : undefined);

  return (
    <>
      {r.kind === RouteRebookingKindEnum.ShortenWait ? (
        <Note>
          Valfritt: väntan {r.waitMinutesBefore} → {r.waitMinutesAfter} min. Paketet går att
          köra som det är.
        </Note>
      ) : (
        <Note>
          Ny tid {formatClock(r.proposedBookedTo)} ({r.shiftMinutes > 0 ? '+' : '−'}
          {Math.abs(r.shiftMinutes)} min), dagen {formatDayHours(r.dayHoursBefore)} →{' '}
          {formatDayHours(r.dayHoursAfter)} h. Paketet går ihop först när kunden sagt ja.
        </Note>
      )}
      {r.transferTransit && (
        <Note>
          Bussen som gör tiden möjlig: avgår{' '}
          {formatClock(new Date(r.transferTransit.departureTimeEpoch * 1000))}, framme{' '}
          {formatClock(new Date(r.transferTransit.arrivalTimeEpoch * 1000))}
          {r.transferTransit.description ? ` (${r.transferTransit.description})` : ''}
          {r.transferTransit.mapUrl && (
            <>
              {' '}
              <a href={r.transferTransit.mapUrl} rel="noreferrer" target="_blank">
                Visa resan
              </a>
            </>
          )}
        </Note>
      )}
      {paired && (
        <Note>
          Hör ihop med {pairedLegNumber ? `ben ${pairedLegNumber} (${paired})` : paired} – dagen
          gäller när båda sagt ja.
        </Note>
      )}
      <ContactRow>
        {primary ? (
          <CallPill
            href={`tel:${primary.phone}`}
            title={`Ring ${primary.role === 'Kund' ? 'kunden' : 'bokaren'}`}
          >
            <FontAwesomeIcon icon={faPhone} />
            {primary.role}
            {primary.name ? ` ${primary.name}` : ''}
            {primary.company ? ` (${primary.company})` : ''} · {primary.phone}
          </CallPill>
        ) : (
          <Muted>Telefonnummer saknas på ärendet</Muted>
        )}
        {secondary && (
          <SecondaryPhone href={`tel:${secondary.phone}`}>
            {secondary.role}
            {secondary.company ? ` (${secondary.company})` : ''} {secondary.phone}
          </SecondaryPhone>
        )}
      </ContactRow>
    </>
  );
};

/** Under adressen när svaret redan är sparat. */
export const LegRebookingConfirmedNote: React.FC<{ confirmed: ConfirmedRebooking }> = ({
  confirmed,
}) => (
  <Note tone="good">
    Kunden sa ja {formatClock(confirmed.confirmedAt)} · flyttad {formatClock(confirmed.oldBookedTo)}{' '}
    → {formatClock(confirmed.newBookedTo)}
  </Note>
);

const ActionsColumn = styled.div`
  position: relative;
  width: ${REBOOKING_ACTIONS_WIDTH_PX}px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  align-self: center;
`;

const ConfirmButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 30px;
  padding: 0 10px;
  border: 0;
  border-radius: 4px;
  background: rgb(29, 122, 63);
  color: #fff;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    background: rgb(190, 190, 190);
    cursor: not-allowed;
  }
`;

const MoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid rgb(205, 205, 205);
  border-radius: 4px;
  background: #fff;
  color: rgb(70, 70, 70);
  cursor: pointer;

  &:disabled {
    color: rgb(170, 170, 170);
    cursor: not-allowed;
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 220px;
  padding: 10px;
  border: 1px solid rgb(205, 205, 205);
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  font-size: 13px;
`;

const MenuRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TimeInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 4px 6px;
  border: 1px solid rgb(190, 190, 190);
  border-radius: 4px;
  font: inherit;
`;

const MenuButton = styled.button<{ negative?: boolean }>`
  padding: 5px 10px;
  border: 1px solid ${({ negative }) => (negative ? 'transparent' : 'rgb(190, 190, 190)')};
  border-radius: 4px;
  background: #fff;
  color: ${({ negative }) => (negative ? 'rgb(180, 35, 24)' : 'inherit')};
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-align: left;

  &:disabled {
    color: rgb(170, 170, 170);
    cursor: not-allowed;
  }
`;

const ConfirmedMark = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 700;
  color: rgb(29, 107, 56);
`;

interface ActionsProps {
  proposal: RouteRebookingProposalDTO;
  /** Saknar skrivrätt (global inställning 106) — knapparna visas men är avstängda. */
  disabled: boolean;
  disabledTitle?: string;
  busy: boolean;
  onAnswer: (proposal: RouteRebookingProposalDTO, answer: RebookingAnswer) => void;
}

/**
 * Längst ut på benet: Bekräfta på den föreslagna tiden, och resten (annan tid, nej) bakom ⋯.
 * Planeringen flyttar aldrig tiden själv — svaret sparas, och paketet räknas om på tiden som
 * står i databasen.
 */
export const LegRebookingActions: React.FC<ActionsProps> = ({
  proposal: r,
  disabled,
  disabledTitle,
  busy,
  onAnswer,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const columnRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(columnRef, () => setMenuOpen(false));
  const proposed = new Date(r.proposedBookedTo);
  const [otherTime, setOtherTime] = useState(formatClock(proposed));
  const otherDate = atClockOnSameDay(new Date(r.currentBookedTo), otherTime);
  const off = disabled || busy;
  const title = disabled ? disabledTitle : undefined;

  const answer = (a: RebookingAnswer) => {
    setMenuOpen(false);
    onAnswer(r, a);
  };

  return (
    <ActionsColumn ref={columnRef}>
      <ConfirmButton
        disabled={off}
        onClick={() => answer({ kind: 'confirm', newBookedTo: proposed })}
        title={title ?? `Kunden sa ja till ${formatClock(proposed)}`}
        type="button"
      >
        {busy ? <TransparentSpinner currentColor small /> : <FontAwesomeIcon icon={faCheck} />}
        Bekräfta
      </ConfirmButton>
      <MoreButton
        aria-expanded={menuOpen}
        aria-label="Fler svar: annan tid eller nej"
        disabled={off}
        onClick={() => setMenuOpen((open) => !open)}
        onKeyDown={(e) => e.key === 'Escape' && setMenuOpen(false)}
        title={title}
        type="button"
      >
        <FontAwesomeIcon icon={faEllipsis} />
      </MoreButton>
      {menuOpen && !off && (
        <Menu>
          <MenuRow>
            <TimeInput
              aria-label="Annan tid samma dag"
              onChange={(e) => setOtherTime(e.target.value)}
              type="time"
              value={otherTime}
            />
            <MenuButton
              disabled={!otherDate}
              onClick={() => otherDate && answer({ kind: 'confirm', newBookedTo: otherDate })}
              title="Den tid kunden gick med på, samma dag"
              type="button"
            >
              Spara
            </MenuButton>
          </MenuRow>
          <MenuButton negative onClick={() => answer({ kind: 'decline' })} type="button">
            Kunden sa nej
          </MenuButton>
        </Menu>
      )}
    </ActionsColumn>
  );
};

/** Svarskolumnen för ett ben vars ja redan är sparat. */
export const LegRebookingConfirmedMark: React.FC = () => (
  <ActionsColumn>
    <ConfirmedMark>
      <FontAwesomeIcon icon={faCheck} />
      Bekräftad
    </ConfirmedMark>
  </ActionsColumn>
);

/** Tom svarskolumn på ben utan förslag, så att tiderna linjerar. */
export const LegRebookingSpacer = styled.span`
  width: ${REBOOKING_ACTIONS_WIDTH_PX}px;
  flex-shrink: 0;
`;
